import { Component, Inject, OnDestroy, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MaterialSharedModule } from '@app/shared/modules/material-shared.module';
import { TranslateService } from '@ngx-translate/core';
import { SharedModule } from '@app/shared/modules/shared.module';
import { StripeFacade } from '@app/store/stripe';
import { Subject, takeUntil, filter, take, combineLatest, pairwise, startWith } from 'rxjs';

export interface ActivateSubscriptionDialogData {
  /** Context: 'pricing' (from button) or 'limit_reached' (during session) */
  context: 'pricing' | 'limit_reached';
  /** Optional plan name for display */
  planName?: string;
}

export interface ActivateSubscriptionDialogResult {
  action: 'activated' | 'cancelled';
}

@Component({
  selector: 'app-activate-subscription-dialog',
  templateUrl: './activate-subscription-dialog.component.html',
  styleUrls: ['./activate-subscription-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MaterialSharedModule,
    SharedModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ActivateSubscriptionDialogComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  
  loading$!: typeof this.stripeFacade.activateSubscriptionLoading$;
  error$!: typeof this.stripeFacade.activateSubscriptionError$;

  constructor(
    public dialogRef: MatDialogRef<ActivateSubscriptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ActivateSubscriptionDialogData,
    private translate: TranslateService,
    private stripeFacade: StripeFacade
  ) {
    this.loading$ = this.stripeFacade.activateSubscriptionLoading$;
    this.error$ = this.stripeFacade.activateSubscriptionError$;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get title(): string {
    if (this.data.context === 'limit_reached') {
      return this.translate.instant('activateSubscriptionDialog.limitReachedTitle');
    }
    return this.translate.instant('activateSubscriptionDialog.title');
  }

  get message(): string {
    if (this.data.context === 'limit_reached') {
      return this.translate.instant('activateSubscriptionDialog.limitReachedMessage');
    }
    return this.translate.instant('activateSubscriptionDialog.message');
  }

  get iconName(): string {
    return this.data.context === 'limit_reached' ? 'hourglass_empty' : 'rocket_launch';
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancelled' });
  }

  onActivate(): void {
    // Dispatch the activation action
    this.stripeFacade.activateSubscription();
    
    // Listen for loading to complete (loading goes from true to false)
    // and check if there's no error - that means success
    this.loading$.pipe(
      takeUntil(this.destroy$),
      startWith(false),
      pairwise(),
      filter(([wasLoading, isLoading]) => wasLoading === true && isLoading === false),
      take(1)
    ).subscribe(() => {
      // Check if there's an error
      this.error$.pipe(take(1)).subscribe(error => {
        if (!error) {
          // Success - close dialog
          this.dialogRef.close({ action: 'activated' });
        }
        // If error, it will be displayed in the template
      });
    });
  }
}
